import { useState, useEffect, useMemo } from 'react';
import { api } from '../../lib/api';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { toast } from 'sonner';
import { TrendingUp, TrendingDown, Minus, Plus, RefreshCw, Edit, Trash2, Eye, BarChart3, Globe, Sparkles, Wand2, MapPin, Users } from 'lucide-react';
import { CityLandingPagesAdmin } from './CityLandingPagesAdmin';

const positionTrend = (history = []) => {
  if (history.length < 2) return 0;
  const last = history[history.length - 1].position;
  const prev = history[history.length - 2].position;
  // lower is better
  return prev - last;
};

const PositionBadge = ({ position }) => {
  if (position == null) {
    return <span className="text-[#1F2A4A]/30 text-sm">—</span>;
  }
  if (position <= 3) return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-700">#{position}</span>;
  if (position <= 10) return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-[#C9B896]/30 text-[#1F2A4A]">#{position}</span>;
  if (position <= 30) return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-500/15 text-amber-700">#{position}</span>;
  return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-[#1F2A4A]/10 text-[#1F2A4A]/60">#{position}</span>;
};

const TrendIndicator = ({ delta }) => {
  if (delta > 0) return <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium"><TrendingUp className="w-3.5 h-3.5" /> +{delta}</span>;
  if (delta < 0) return <span className="inline-flex items-center gap-1 text-red-500 text-xs font-medium"><TrendingDown className="w-3.5 h-3.5" /> {delta}</span>;
  return <span className="inline-flex items-center gap-1 text-[#1F2A4A]/40 text-xs"><Minus className="w-3.5 h-3.5" /> 0</span>;
};

// Tiny SVG sparkline from a list of history points (positions). Lower is better
// so we invert visually — higher line = better rank.
const Sparkline = ({ points, width = 90, height = 28 }) => {
  if (!points || points.length < 2) {
    return <span className="text-[#1F2A4A]/30 text-xs italic">pas encore d'historique</span>;
  }
  const values = points.map((p) => p.position);
  const max = Math.max(...values, 50);
  const min = Math.min(...values, 1);
  const range = Math.max(1, max - min);
  const stepX = width / (values.length - 1);
  // Invert Y so lower position (better) is up
  const path = values
    .map((v, i) => {
      const x = i * stepX;
      const y = ((v - min) / range) * (height - 4) + 2;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${(height - y).toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg width={width} height={height} className="overflow-visible">
      <path d={path} stroke="#C9B896" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

// Bar chart for visits per day (last 30d)
const VisitsBars = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="text-center py-12 text-[#1F2A4A]/40 text-sm italic">
        Aucune visite enregistrée pour l'instant.
      </div>
    );
  }
  const max = Math.max(...data.map((d) => d.count), 1);
  return (
    <div className="flex items-end gap-1 h-32" data-testid="seo-visits-chart">
      {data.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
          <div
            className="w-full bg-gradient-to-t from-[#C9B896] to-[#E0D4BA] rounded-t hover:from-[#1F2A4A] hover:to-[#324166] transition-colors cursor-help"
            style={{ height: `${(d.count / max) * 100}%`, minHeight: '2px' }}
            title={`${d.date} · ${d.count} visite${d.count > 1 ? 's' : ''}`}
          />
        </div>
      ))}
    </div>
  );
};

const EMPTY_FORM = { keyword: '', target_url: '', search_volume: 0, notes: '' };

export const SeoTrackingAdmin = () => {
  const [keywords, setKeywords] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  // ── Visitors analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  // ── Keyword generator (Gemini)
  const [genOpen, setGenOpen] = useState(false);
  const [genTopic, setGenTopic] = useState('');
  const [genLoading, setGenLoading] = useState(false);
  const [genResults, setGenResults] = useState([]);
  const [adding, setAdding] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const [kws, st, an] = await Promise.all([
        api.listSeoKeywords(),
        api.getSeoStats(),
        api.getAnalyticsStats(analyticsDays).catch(() => null),
      ]);
      setKeywords(kws || []);
      setStats(st || null);
      setAnalytics(an || null);
    } catch (e) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    // Reload analytics only when the days window changes
    api.getAnalyticsStats(analyticsDays).then(setAnalytics).catch(() => {});
  }, [analyticsDays]);

  // ── Keyword generator handlers
  const runGenerator = async () => {
    if (!genTopic.trim()) { toast.error("Indique un sujet"); return; }
    setGenLoading(true);
    setGenResults([]);
    try {
      const data = await api.generateSeoKeywords(genTopic.trim(), 12);
      setGenResults(data.keywords || []);
      if ((data.keywords || []).length === 0) toast.warning("Aucun mot-clé généré");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur Gemini");
    } finally {
      setGenLoading(false);
    }
  };

  const addGeneratedKeyword = async (k) => {
    setAdding((m) => ({ ...m, [k.keyword]: true }));
    try {
      await api.createSeoKeyword({
        keyword: k.keyword,
        target_url: '',
        search_volume: k.volume || 0,
        notes: `[Gemini · ${k.intent}·${k.difficulty}] ${k.why || ''}`.slice(0, 240),
      });
      toast.success(`Ajouté : ${k.keyword}`);
      // Mark as added so the row collapses visually
      setGenResults((prev) => prev.map((x) => x.keyword === k.keyword ? { ...x, _added: true } : x));
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Déjà suivi ?");
    } finally {
      setAdding((m) => ({ ...m, [k.keyword]: false }));
    }
  };

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (kw) => {
    setEditing(kw);
    setForm({
      keyword: kw.keyword,
      target_url: kw.target_url || '',
      search_volume: kw.search_volume || 0,
      current_position: kw.current_position || '',
      notes: kw.notes || '',
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.keyword?.trim()) { toast.error("Mot-clé requis"); return; }
    try {
      if (editing) {
        const payload = { ...form };
        if (payload.current_position === '' || payload.current_position == null) {
          delete payload.current_position;
        } else {
          payload.current_position = parseInt(payload.current_position);
        }
        payload.search_volume = parseInt(payload.search_volume) || 0;
        await api.updateSeoKeyword(editing.id, payload);
        toast.success("Mot-clé mis à jour");
      } else {
        await api.createSeoKeyword({
          keyword: form.keyword.trim(),
          target_url: form.target_url.trim(),
          search_volume: parseInt(form.search_volume) || 0,
          notes: form.notes,
        });
        toast.success("Mot-clé ajouté au suivi");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleCheck = async (id) => {
    try {
      const updated = await api.checkSeoKeyword(id);
      setKeywords((prev) => prev.map((k) => k.id === id ? updated : k));
      toast.success(`Position relevée : #${updated.current_position}`);
    } catch (_) { toast.error("Erreur lors de la vérification"); }
  };

  const handleCheckAll = async () => {
    setRefreshing(true);
    try {
      await api.checkAllSeoKeywords();
      await load();
      toast.success("Positions rafraîchies");
    } catch (_) { toast.error("Erreur"); }
    finally { setRefreshing(false); }
  };

  const handleDelete = async (kw) => {
    if (!window.confirm(`Supprimer "${kw.keyword}" du suivi ?`)) return;
    try {
      await api.deleteSeoKeyword(kw.id);
      toast.success("Supprimé");
      load();
    } catch (_) { toast.error("Erreur"); }
  };

  const summary = useMemo(() => {
    if (!stats) return null;
    return {
      tracked: stats.total_keywords,
      top3: stats.top_3_count,
      top10: stats.top_10_count,
      visits30d: (stats.per_day || []).reduce((acc, d) => acc + d.count, 0),
    };
  }, [stats]);

  return (
    <div className="space-y-8" data-testid="seo-tracking-admin">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-2">SUIVI GOOGLE</p>
          <h2 className="font-light text-3xl md:text-4xl text-[#1F2A4A] tracking-tight">
            Positionnement <span className="font-serif italic text-[#C9B896]">et visites.</span>
          </h2>
          <p className="text-sm text-[#1F2A4A]/55 mt-2 max-w-xl">
            Mots-clés cibles, positions Google et trafic du blog. Mode <strong>{stats?.mode === 'live' ? 'live (SerpApi)' : 'démonstration'}</strong> —
            les positions sont {stats?.mode === 'live' ? 'récupérées en direct depuis Google via SerpApi' : 'éditables manuellement ou simulées'}.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => setGenOpen(true)}
            className="rounded-full border-[#C9B896]/40 text-[#1F2A4A] hover:bg-[#C9B896]/15"
            data-testid="seo-generate-keywords"
          >
            <Wand2 className="w-4 h-4 mr-2" /> Générer des mots-clés
          </Button>
          <Button
            variant="outline"
            onClick={handleCheckAll}
            disabled={refreshing}
            className="rounded-full border-[#1F2A4A]/20 text-[#1F2A4A] hover:bg-[#C9B896]/10"
            data-testid="seo-refresh-all"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Rafraîchir
          </Button>
          <Button
            onClick={openCreate}
            className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full"
            data-testid="seo-create-keyword"
          >
            <Plus className="w-4 h-4 mr-2" /> Nouveau mot-clé
          </Button>
        </div>
      </div>

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-5" data-testid="seo-kpi-tracked">
            <Eye className="w-4 h-4 text-[#C9B896] mb-3" />
            <p className="font-serif italic text-3xl text-[#1F2A4A] leading-none">{summary.tracked}</p>
            <p className="text-sm font-medium text-[#1F2A4A] mt-2">Mots-clés suivis</p>
          </div>
          <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-5" data-testid="seo-kpi-top3">
            <Sparkles className="w-4 h-4 text-[#C9B896] mb-3" />
            <p className="font-serif italic text-3xl text-[#1F2A4A] leading-none">{summary.top3}</p>
            <p className="text-sm font-medium text-[#1F2A4A] mt-2">Top 3 Google</p>
          </div>
          <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-5" data-testid="seo-kpi-top10">
            <TrendingUp className="w-4 h-4 text-[#C9B896] mb-3" />
            <p className="font-serif italic text-3xl text-[#1F2A4A] leading-none">{summary.top10}</p>
            <p className="text-sm font-medium text-[#1F2A4A] mt-2">Top 10 Google</p>
          </div>
          <div className="bg-white border border-[#1F2A4A]/10 rounded-xl p-5" data-testid="seo-kpi-visits">
            <BarChart3 className="w-4 h-4 text-[#C9B896] mb-3" />
            <p className="font-serif italic text-3xl text-[#1F2A4A] leading-none">{summary.visits30d}</p>
            <p className="text-sm font-medium text-[#1F2A4A] mt-2">Visites blog · 30j</p>
          </div>
        </div>
      )}

      {/* Visits chart */}
      <div className="bg-white border border-[#1F2A4A]/10 rounded-2xl p-6">
        <div className="flex items-baseline justify-between mb-5">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896]">VISITES QUOTIDIENNES</p>
            <h3 className="font-serif text-xl text-[#1F2A4A] mt-1">30 derniers jours</h3>
          </div>
          <span className="text-xs text-[#1F2A4A]/50">{stats?.per_day?.length || 0} jours avec trafic</span>
        </div>
        <VisitsBars data={stats?.per_day || []} />
      </div>

      {/* Top posts */}
      {stats?.per_post?.length > 0 && (
        <div className="bg-white border border-[#1F2A4A]/10 rounded-2xl p-6">
          <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896] mb-4">PAGES LES PLUS LUES</p>
          <ul className="space-y-3" data-testid="seo-top-posts">
            {stats.per_post.slice(0, 5).map((p) => (
              <li key={p.slug} className="flex items-center justify-between text-sm">
                <span className="text-[#1F2A4A] truncate flex-1 mr-3 font-mono text-xs">{p.slug}</span>
                <span className="text-[#C9B896] font-medium">{p.count} vues</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ─── VISITEURS RÉELS — site-wide analytics ─── */}
      <div className="bg-white border border-[#1F2A4A]/10 rounded-2xl p-6" data-testid="visitors-panel">
        <div className="flex flex-wrap items-baseline justify-between gap-4 mb-5">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896]">VISITEURS DU SITE</p>
            <h3 className="font-serif text-xl text-[#1F2A4A] mt-1">Trafic réel · {analyticsDays} derniers jours</h3>
          </div>
          <div className="flex items-center gap-2">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setAnalyticsDays(d)}
                className={`text-xs px-3 py-1.5 rounded-full transition-all ${
                  analyticsDays === d
                    ? 'bg-[#1F2A4A] text-[#E8DCC8] font-medium'
                    : 'text-[#1F2A4A]/60 hover:bg-[#1F2A4A]/5'
                }`}
                data-testid={`analytics-range-${d}`}
              >
                {d}j
              </button>
            ))}
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-[#FAF7F0]/60 rounded-xl p-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mb-1">Cumul historique</p>
            <p className="font-serif italic text-2xl text-[#1F2A4A]" data-testid="analytics-total">
              {(analytics?.total_visits ?? 0).toLocaleString('fr-FR')}
            </p>
          </div>
          <div className="bg-[#FAF7F0]/60 rounded-xl p-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mb-1">Sur la période</p>
            <p className="font-serif italic text-2xl text-[#1F2A4A]" data-testid="analytics-window">
              {(analytics?.window_visits ?? 0).toLocaleString('fr-FR')}
            </p>
          </div>
          <div className="bg-[#FAF7F0]/60 rounded-xl p-4">
            <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#C9B896] mb-1">Visiteurs uniques</p>
            <p className="font-serif italic text-2xl text-[#1F2A4A]" data-testid="analytics-unique">
              {(analytics?.window_unique_visitors ?? 0).toLocaleString('fr-FR')}
            </p>
          </div>
        </div>

        {/* Curve */}
        <VisitsBars data={analytics?.per_day || []} />

        {/* Country + path two-column */}
        <div className="grid md:grid-cols-2 gap-6 mt-7">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9B896] mb-3 flex items-center gap-2">
              <MapPin className="w-3 h-3" /> Pays de connexion
            </p>
            {(analytics?.per_country || []).length === 0 ? (
              <p className="text-sm text-[#1F2A4A]/40 italic">Pas encore de données géographiques.</p>
            ) : (
              <ul className="space-y-2" data-testid="analytics-countries">
                {analytics.per_country.slice(0, 10).map((c) => {
                  const max = analytics.per_country[0].count;
                  const pct = Math.max(2, Math.round((c.count / max) * 100));
                  return (
                    <li key={c.country_code} className="text-sm">
                      <div className="flex justify-between mb-1">
                        <span className="text-[#1F2A4A] flex items-center gap-2">
                          {c.country_code && (
                            <img
                              src={`https://flagcdn.com/w20/${c.country_code.toLowerCase()}.png`}
                              alt={c.country || c.country_code}
                              className="h-3 w-auto rounded-sm"
                              loading="lazy"
                            />
                          )}
                          {c.country || c.country_code}
                        </span>
                        <span className="text-[#C9B896] font-medium">{c.count}</span>
                      </div>
                      <div className="h-1.5 bg-[#1F2A4A]/8 rounded-full overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#C9B896] to-[#E0D4BA]" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#C9B896] mb-3 flex items-center gap-2">
              <Users className="w-3 h-3" /> Pages les plus visitées
            </p>
            {(analytics?.per_path || []).length === 0 ? (
              <p className="text-sm text-[#1F2A4A]/40 italic">Aucune donnée pour cette période.</p>
            ) : (
              <ul className="space-y-2" data-testid="analytics-paths">
                {analytics.per_path.slice(0, 10).map((p) => (
                  <li key={p.path} className="flex justify-between text-sm">
                    <span className="text-[#1F2A4A] font-mono text-xs truncate flex-1 mr-3">{p.path}</span>
                    <span className="text-[#C9B896] font-medium">{p.count}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* Keywords table */}
      <div className="bg-white border border-[#1F2A4A]/10 rounded-2xl overflow-hidden">
        <div className="px-6 py-5 border-b border-[#1F2A4A]/10 flex items-baseline justify-between">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.3em] text-[#C9B896]">MOTS-CLÉS</p>
            <h3 className="font-serif text-xl text-[#1F2A4A] mt-1">Suivi de positionnement</h3>
          </div>
          <span className="text-xs text-[#1F2A4A]/50">{keywords.length} suivi{keywords.length > 1 ? 's' : ''}</span>
        </div>
        {loading ? (
          <div className="p-12 text-center text-[#1F2A4A]/40 text-sm">Chargement…</div>
        ) : keywords.length === 0 ? (
          <div className="p-16 text-center">
            <Globe className="w-14 h-14 text-[#C9B896] mx-auto mb-4 opacity-40" />
            <h4 className="font-serif text-xl text-[#1F2A4A] mb-2">Aucun mot-clé suivi</h4>
            <p className="text-sm text-[#1F2A4A]/60 mb-5">Ajoute un mot-clé pour commencer à suivre ta position sur Google.</p>
            <Button onClick={openCreate} className="bg-[#C9B896] text-[#1F2A4A] hover:bg-[#E0D4BA] rounded-full">
              <Plus className="w-4 h-4 mr-2" /> Premier mot-clé
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#1F2A4A]/10 bg-[#FAF7F0]/50">
                  <th className="text-left px-6 py-3 text-xs font-mono uppercase tracking-wider text-[#1F2A4A]/50">Mot-clé</th>
                  <th className="text-left px-3 py-3 text-xs font-mono uppercase tracking-wider text-[#1F2A4A]/50">Volume/mois</th>
                  <th className="text-left px-3 py-3 text-xs font-mono uppercase tracking-wider text-[#1F2A4A]/50">Position</th>
                  <th className="text-left px-3 py-3 text-xs font-mono uppercase tracking-wider text-[#1F2A4A]/50">Meilleure</th>
                  <th className="text-left px-3 py-3 text-xs font-mono uppercase tracking-wider text-[#1F2A4A]/50">Tendance</th>
                  <th className="text-left px-3 py-3 text-xs font-mono uppercase tracking-wider text-[#1F2A4A]/50">Évolution</th>
                  <th className="text-right px-6 py-3 text-xs font-mono uppercase tracking-wider text-[#1F2A4A]/50">Actions</th>
                </tr>
              </thead>
              <tbody>
                {keywords.map((kw) => (
                  <tr key={kw.id} className="border-b border-[#1F2A4A]/8 last:border-0 hover:bg-[#FAF7F0]/40 transition-colors" data-testid={`seo-row-${kw.id}`}>
                    <td className="px-6 py-4">
                      <div className="font-medium text-[#1F2A4A]">{kw.keyword}</div>
                      {kw.target_url && (
                        <div className="text-[10px] text-[#1F2A4A]/50 font-mono mt-0.5 truncate max-w-xs">→ {kw.target_url}</div>
                      )}
                    </td>
                    <td className="px-3 py-4 text-sm text-[#1F2A4A]/70">
                      {kw.search_volume ? kw.search_volume.toLocaleString('fr-FR') : <span className="text-[#1F2A4A]/30">—</span>}
                    </td>
                    <td className="px-3 py-4"><PositionBadge position={kw.current_position} /></td>
                    <td className="px-3 py-4"><PositionBadge position={kw.best_position} /></td>
                    <td className="px-3 py-4"><TrendIndicator delta={positionTrend(kw.positions)} /></td>
                    <td className="px-3 py-4"><Sparkline points={kw.positions} /></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleCheck(kw.id)}
                          title="Vérifier la position"
                          className="w-8 h-8 rounded-full hover:bg-[#C9B896]/15 flex items-center justify-center text-[#1F2A4A]/70 hover:text-[#C9B896] transition-colors"
                          data-testid={`seo-check-${kw.id}`}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => openEdit(kw)}
                          title="Modifier"
                          className="w-8 h-8 rounded-full hover:bg-[#1F2A4A]/5 flex items-center justify-center text-[#1F2A4A]/70 hover:text-[#1F2A4A] transition-colors"
                          data-testid={`seo-edit-${kw.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(kw)}
                          title="Supprimer"
                          className="w-8 h-8 rounded-full hover:bg-red-50 flex items-center justify-center text-red-500 transition-colors"
                          data-testid={`seo-delete-${kw.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">
              {editing ? 'Modifier le mot-clé' : 'Nouveau mot-clé'}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? 'Ajuste manuellement la position ou le volume.'
                : 'Ajoute un mot-clé à suivre sur Google. La position se renseigne ensuite, manuellement ou via la vérification automatique.'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-5 pt-2">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Mot-clé</Label>
              <Input
                value={form.keyword}
                onChange={(e) => setForm({ ...form, keyword: e.target.value })}
                placeholder="formation sellerie nautique"
                className="bg-transparent border-[#1F2A4A]/15"
                data-testid="seo-form-keyword"
                required
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-[#C9B896]">URL cible (ce qu'on veut faire monter)</Label>
              <Input
                value={form.target_url}
                onChange={(e) => setForm({ ...form, target_url: e.target.value })}
                placeholder="/fr/formation/nautique"
                className="bg-transparent border-[#1F2A4A]/15"
                data-testid="seo-form-url"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Volume mensuel (estim.)</Label>
                <Input
                  type="number"
                  value={form.search_volume}
                  onChange={(e) => setForm({ ...form, search_volume: e.target.value })}
                  className="bg-transparent border-[#1F2A4A]/15"
                  data-testid="seo-form-volume"
                />
              </div>
              {editing && (
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Position actuelle</Label>
                  <Input
                    type="number"
                    min="1"
                    max="200"
                    value={form.current_position ?? ''}
                    onChange={(e) => setForm({ ...form, current_position: e.target.value })}
                    placeholder="Ex: 7"
                    className="bg-transparent border-[#1F2A4A]/15"
                    data-testid="seo-form-position"
                  />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-[#C9B896]">Notes</Label>
              <Textarea
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Pourquoi ce mot-clé compte, hypothèses, intentions…"
                className="bg-transparent border-[#1F2A4A]/15 min-h-[80px]"
                data-testid="seo-form-notes"
              />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Annuler</Button>
              <Button type="submit" className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full" data-testid="seo-form-submit">
                {editing ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Keyword generator dialog (Gemini) ── */}
      <Dialog open={genOpen} onOpenChange={(v) => { setGenOpen(v); if (!v) { setGenResults([]); setGenTopic(''); } }}>
        <DialogContent className="max-w-3xl bg-white" data-testid="seo-keyword-gen-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl flex items-center gap-2">
              <Wand2 className="w-5 h-5 text-[#C9B896]" /> Générateur de mots-clés
            </DialogTitle>
            <DialogDescription>
              Décris un sujet / une formation. Gemini propose 12 mots-clés réellement tapés sur Google,
              avec volume estimé, intention et difficulté. Tu peux les ajouter au suivi en un clic.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 pt-2">
            <div className="flex gap-3">
              <Input
                value={genTopic}
                onChange={(e) => setGenTopic(e.target.value)}
                placeholder="ex: formation sellerie automobile, restauration cuir Istres, café racer..."
                className="bg-transparent border-[#1F2A4A]/15 flex-1"
                data-testid="seo-gen-topic-input"
                onKeyDown={(e) => { if (e.key === 'Enter') runGenerator(); }}
              />
              <Button
                onClick={runGenerator}
                disabled={genLoading}
                className="bg-[#1F2A4A] text-[#FAF7F0] hover:bg-[#324166] rounded-full whitespace-nowrap"
                data-testid="seo-gen-run"
              >
                {genLoading ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                Générer
              </Button>
            </div>

            {genLoading && (
              <p className="text-sm text-[#1F2A4A]/50 italic">Gemini analyse les requêtes Google... ~5s</p>
            )}

            {genResults.length > 0 && (
              <div className="border border-[#1F2A4A]/10 rounded-xl overflow-hidden">
                <table className="w-full text-sm" data-testid="seo-gen-results">
                  <thead>
                    <tr className="bg-[#FAF7F0]/60 text-[10px] uppercase tracking-wider text-[#1F2A4A]/50 font-mono">
                      <th className="text-left px-4 py-2.5">Mot-clé</th>
                      <th className="text-left px-3 py-2.5">Volume</th>
                      <th className="text-left px-3 py-2.5">Intent</th>
                      <th className="text-left px-3 py-2.5">Difficulté</th>
                      <th className="text-right px-4 py-2.5">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {genResults.map((k) => (
                      <tr key={k.keyword} className="border-t border-[#1F2A4A]/8">
                        <td className="px-4 py-3">
                          <div className="font-medium text-[#1F2A4A]">{k.keyword}</div>
                          {k.why && <div className="text-[10px] text-[#1F2A4A]/50 mt-0.5">{k.why}</div>}
                        </td>
                        <td className="px-3 py-3 text-[#1F2A4A]/70">{k.volume?.toLocaleString('fr-FR') || '—'}</td>
                        <td className="px-3 py-3">
                          <span className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#1F2A4A]/8 text-[#1F2A4A]/70">{k.intent}</span>
                        </td>
                        <td className="px-3 py-3">
                          <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full ${
                            k.difficulty === 'low' ? 'bg-emerald-500/15 text-emerald-700' :
                            k.difficulty === 'high' ? 'bg-red-500/10 text-red-600' :
                            'bg-amber-500/15 text-amber-700'
                          }`}>{k.difficulty}</span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          {k._added ? (
                            <span className="text-[10px] uppercase tracking-wider text-emerald-600">✓ ajouté</span>
                          ) : (
                            <button
                              onClick={() => addGeneratedKeyword(k)}
                              disabled={!!adding[k.keyword]}
                              className="text-[10px] uppercase tracking-wider text-[#1F2A4A] hover:text-[#C9B896] transition-colors"
                              data-testid={`seo-gen-add-${k.keyword.slice(0, 20)}`}
                            >
                              {adding[k.keyword] ? '...' : '+ suivre'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Local SEO city landing pages ── */}
      <div className="mt-16 pt-10 border-t border-[#1F2A4A]/10">
        <CityLandingPagesAdmin />
      </div>
    </div>
  );
};

export default SeoTrackingAdmin;
